import { useBusinessQuery } from '../../../api/businesses';
import { KybFlowStep } from './KYB/KybFlowStep';
import { useState, useEffect } from 'react';
import { MainLayout } from '../../Layout/MainLayout';
import { useTranslation } from 'react-i18next';
import { Box, BrightyStepButton, Grid, Step, Stepper } from '@razz-team/ui-web';
import { ApiCustomerBusiness } from '../../../openapi';
import { OwnerVerificationStep } from './KYB/OwnerVerificationStep';
import { BusinessVerificationStep } from './KYB/BusinessVerificationStep';

enum VerificationSteps {
  businessInfo,
  ownerInfo,
}

function VerificationStep({
  step,
  onStepChange,
}: {
  step: VerificationSteps;
  onStepChange: (step: VerificationSteps) => void;
}) {
  switch (step) {
    case VerificationSteps.businessInfo:
      return (
        <BusinessVerificationStep
          onSuccess={() => {
            onStepChange(VerificationSteps.ownerInfo);
          }}
        />
      );
    case VerificationSteps.ownerInfo:
      return <OwnerVerificationStep />;
    default:
      return null;
  }
}

function calculateCurrentStep({ business }: { business: ApiCustomerBusiness }): VerificationSteps {
  if (business.state === 'CREATED') {
    return VerificationSteps.businessInfo;
  }

  return VerificationSteps.ownerInfo;
}

export function VerificationPage() {
  const { t } = useTranslation();
  const businessQuery = useBusinessQuery();

  const [step, setStep] = useState<VerificationSteps | undefined>(undefined);

  const business = businessQuery.data?.business;

  // calculate the current step based on data when queries load
  useEffect(() => {
    setStep((prevStep) => {
      if (prevStep !== undefined) {
        return prevStep; // don't overwrite if already set
      }

      if (business) {
        return calculateCurrentStep({ business });
      }

      return prevStep;
    });
  }, [business]);

  if (!businessQuery.isSuccess) return null;
  if (!business?.verificationFlow) {
    return (
      <MainLayout title={t('Choose your business type')}>
        <KybFlowStep onSuccess={() => setStep(VerificationSteps.businessInfo)} />
      </MainLayout>
    );
  }

  if (business.verificationFlow === 'FREELANCER') {
    return (
      <MainLayout title={t('Set up your freelance account')}>
        <OwnerVerificationStep />
      </MainLayout>
    );
  }

  if (step === undefined) {
    return null;
  }

  return (
    <MainLayout title={t('Set up your company account')}>
      <Grid size={12}>
        <Box width="100%">
          <Stepper alternativeLabel activeStep={step} nonLinear>
            <Step>
              <BrightyStepButton
                value={VerificationSteps.businessInfo}
                onClick={() => setStep(VerificationSteps.businessInfo)}
              >
                {t('Business verification')}
              </BrightyStepButton>
            </Step>
            <Step>
              <BrightyStepButton
                value={VerificationSteps.ownerInfo}
                onClick={() => setStep(VerificationSteps.ownerInfo)}
              >
                {t('Owner verification')}
              </BrightyStepButton>
            </Step>
          </Stepper>
        </Box>
      </Grid>
      <Grid size={12}>
        <VerificationStep step={step} onStepChange={setStep} />
      </Grid>
    </MainLayout>
  );
}
